import { Download, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { useInstallModalStore } from '../../store/installModalStore';
import { usePwaInstalled } from '../../hooks/usePwaInstalled';
import Button from '../ui/Button';
import ShippingBadge from '../ui/ShippingBadge';
import SupportBadge from '../ui/SupportBadge';
import WarrantyBadge from '../ui/WarrantyBadge';
import TrustBadgePill from '../ui/TrustBadgePill';
import HeroPromoVideoCard from './HeroPromoVideoCard';

const MOBILE_TRUST_BADGES = [ShippingBadge, SupportBadge, WarrantyBadge];

function MobileTrustBadges() {
  const { t } = useI18n();
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const programmaticRef = useRef(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % MOBILE_TRUST_BADGES.length);
    }, 3200);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    programmaticRef.current = true;
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
    const t = window.setTimeout(() => {
      programmaticRef.current = false;
    }, 600);
    return () => {
      window.clearTimeout(t);
      programmaticRef.current = false;
    };
  }, [index]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => {
      if (programmaticRef.current) return;
      const i = Math.round(el.scrollLeft / el.clientWidth);
      if (i < 0 || i >= MOBILE_TRUST_BADGES.length) return;
      setIndex((prev) => (prev === i ? prev : i));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      ref={trackRef}
      className="trust-badges-carousel flex w-full snap-x snap-mandatory overflow-x-auto scroll-smooth pt-2"
      aria-roledescription="carousel"
      aria-label={t('trust_badges')}
    >
      {MOBILE_TRUST_BADGES.map((Badge, i) => (
        <div key={i} className="flex w-full shrink-0 snap-center items-center justify-center">
          <Badge />
        </div>
      ))}
    </div>
  );
}


export default function HeroSection() {
  const { t } = useI18n();
  const installed = usePwaInstalled();
  const openInstallModal = useInstallModalStore((state) => state.openInstallModal);
  const setFloatingVisible = useInstallModalStore((state) => state.setFloatingVisible);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      const heroBottom = heroRef.current.getBoundingClientRect().bottom;
      const snapTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--snap-top') || '0', 10) || 0;
      setFloatingVisible(heroBottom <= snapTop);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [setFloatingVisible]);

return (
    <section ref={heroRef} data-testid="storefront-hero" className="home-snap-section relative flex flex-col px-5 sm:px-6 lg:px-10 min-h-[calc(100dvh-var(--snap-top,4rem))] pt-5 pb-4 sm:min-h-[calc(100dvh-var(--snap-top,4rem))] md:py-10 lg:min-h-[calc(100dvh-var(--snap-top,5rem))]">
      <div className="mx-auto flex w-full max-w-[1800px] flex-1 flex-col pb-10 lg:pb-0">
        <div className="flex flex-1 flex-col gap-3 lg:flex-none lg:flex-row lg:items-start lg:gap-5">
          <div className="flex flex-1 flex-col items-center gap-y-2.5 text-center lg:w-5/12 lg:flex-1 lg:flex-col lg:justify-start lg:gap-y-3 lg:text-left">
            <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-5 text-center lg:flex-none lg:items-start lg:justify-start lg:gap-3 lg:text-left">
              <div className="flex w-full flex-col items-center space-y-1.5 lg:space-y-3 text-center lg:items-start lg:text-left">
                <TrustBadgePill id="hero_delivery" className="badge-truck-drive mb-1 self-start lg:-mt-2" />
                <div className="hero-copy-stage space-y-2 flex-1">
                  <h1 id="hero-title" className="font-display text-[clamp(1.55rem,6vw,4rem)] font-bold leading-[0.96]">
                    <span className="bg-gradient-to-r from-brand via-accent to-brand bg-clip-text text-transparent">{t('hero_title1')}</span>
                    <br />
                    <span className="text-ink">{t('hero_title2')}</span>
                  </h1>
                  <p className="mx-auto max-w-lg text-sm font-medium leading-relaxed drop-shadow-sm md:text-lg bg-gradient-to-r from-ink via-ink/95 to-highlight bg-clip-text text-transparent">{t('hero_tagline')}</p>
                </div>
              </div>
              <div className="lg:hidden w-full pt-1">
                <MobileTrustBadges />
              </div>
            </div>
            <div className="lg:hidden flex flex-1 w-full min-h-0 flex-col items-center pb-20">
              {!installed && (
                <div className="flex min-h-0 flex-1 w-full flex-col items-center justify-center gap-4">
                  <Button
                    variant="glass-primary"
                    size="sm"
                    className="install-button pointer-events-auto h-12 rounded-full install-label-expanded border border-highlight/35"
                    onClick={() => openInstallModal()}
                  >
                    <Download className="install-icon h-5 w-5 shrink-0" aria-hidden="true" />
                    <span className="install-label install-label-visible whitespace-nowrap">{t('pwa_download_application')}</span>
                  </Button>
                  <div className="mx-auto h-px w-full max-w-sm bg-gradient-to-r from-transparent via-ink/15 to-transparent" aria-hidden="true" />
                </div>
              )}
              <HeroPromoVideoCard variant="mobile" />
            </div>
          </div>
          <div className="hidden lg:flex flex-1 min-h-0 lg:items-center lg:flex-col">
            <HeroPromoVideoCard />
          </div>
        </div>
        {!installed && (
          <div className="hidden justify-center py-8 lg:flex">
            <Button
              variant="glass-primary"
              size="sm"
              className="install-button pointer-events-auto h-12 rounded-full install-label-expanded border border-highlight/35"
              onClick={() => openInstallModal()}
            >
              <Download className="install-icon h-5 w-5 shrink-0" aria-hidden="true" />
              <span className="install-label install-label-visible whitespace-nowrap">{t('pwa_download_application')}</span>
            </Button>
          </div>
        )}
        <div className="hidden lg:flex justify-center gap-1.5 pt-0 pb-14 lg:pb-16">
          <ShippingBadge />
          <SupportBadge />
          <WarrantyBadge />
        </div>
        <div className="mt-auto flex flex-col items-center justify-center gap-2 pointer-events-none pt-2">
          <button
            type="button"
            className="pointer-events-auto inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted/80 transition-colors hover:text-brand"
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          >
            {t('view_products')}
            <ChevronDown className="h-4 w-4 animate-bounce" />
          </button>
        </div>
      </div>
    </section>
  );
}
